/**
 * RiskExamMigrationPage
 * Unified page for migrating risks and exams across organizational levels
 * Combines the functionality of RiskApplicationPage and ExamApplicationPage
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  Chip,
  Stack,
  ListItemText,
  FormHelperText,
  Link,
} from '@mui/material';
import { Warning as WarningIcon, Science as ExamIcon } from '@mui/icons-material';
import { PageShell } from '../../components';
import { FaixaDeAlcance } from '../../components/applications/FaixaDeAlcance';
import { ICONE_DO_NIVEL } from '../../components/applications/iconeDoNivel';
import { pode } from '../../authorization/pode';
import { useControle, useVeredito } from '../../authorization/useVeredito';
import { useDadoPermitido, useListaPermitida } from '../../authorization/useListaPermitida';
import { DualTransferList, ExamApplicationModal } from '../../components/exam-applications';
import { RiskApplicationModal, ExposureLevelBadge } from '../../components/risk-applications';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { occupationService } from '../../services/occupationService';
import { employeeService } from '../../services/employeeService';
import { unitService } from '../../services/unitService';
import {
  ApplicationLevel,
  ApplicationLevelLabels,
  ApplicationData,
  ExamWithApplication,
  AvailableExam,
  isExamWithApplication,
} from '../../types/examApplication';
import {
  RiskWithApplication,
  AvailableRisk,
  ExposureLevel,
  isRiskWithApplication,
} from '../../types/riskApplication';
import type { RiskApplicationScope } from '../../types/riskApplication';
import { useExamApplications } from '../exam-applications/hooks/useExamApplications';
import { useAplicarExamesDireto, type GravidadeDoAviso } from '../exam-applications/useAplicarExamesDireto';
import { destinoDaBusca } from '../risk-applications/empresaDaBusca';
import { useRiskApplications } from '../risk-applications/hooks/useRiskApplications';

// ============================================================================
// TYPES
// ============================================================================

interface SelectOption {
  id: number;
  name: string;
  parentId?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel({ children, value, index }: TabPanelProps): React.ReactElement | null {
  if (value !== index) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}


// ============================================================================
// COMPONENT
// ============================================================================

export const RiskExamMigrationPage: React.FC = () => {
  // Tab state
  const [activeMainTab, setActiveMainTab] = useState<number>(0);
  const [activeSubTab, setActiveSubTab] = useState<number>(0);

  // Exam application state from hook
  const {
    applied: examApplied,
    available: examAvailable,
    loading: examLoading,
    error: examError,
    loadExams,
    applyMultipleExams,
    removeMultipleExams,
    updateApplication: updateExamApplication,
    clearError: clearExamError,
  } = useExamApplications();

  // Risk application state from hook
  const {
    applied: riskApplied,
    available: riskAvailable,
    alerts: riskAlerts,
    loading: riskLoading,
    loadingAlerts: riskLoadingAlerts,
    error: riskError,
    alertsError: riskAlertsError,
    loadRisks,
    loadAlerts: loadRiskAlerts,
    removeMultipleRisks,
    clearError: clearRiskError,
    clearAlertsError: clearRiskAlertsError,
  } = useRiskApplications();

  const { search } = useLocation();

  // Local UI state
  const [destinoInicial] = useState(() => destinoDaBusca(search));
  const [selectedLevel, setSelectedLevel] = useState<ApplicationLevel>(
    destinoInicial.nivel ?? ApplicationLevel.COMPANY
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    destinoInicial.companyId
  );
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(destinoInicial.unitId);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
  const [selectedOccupationId, setSelectedOccupationId] = useState<number | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  // Options for selectors
  const [companies, setCompanies] = useState<SelectOption[]>([]);
  const [departments, setDepartments] = useState<SelectOption[]>([]);
  const [occupations, setOccupations] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const cadastrarUnidade = useVeredito('/units', pode('unit.create'));
  const abrirConvocacao = useControle('/exam-summons', pode('exam_summons.read'), 'Abrir Convocação de Exames');

  // Exam Modal state
  const [examModalOpen, setExamModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<
    AvailableExam | ExamWithApplication | null
  >(null);

  // Risk Modal state
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [riskModalMode, setRiskModalMode] = useState<'apply' | 'edit'>('apply');
  const [selectedRisk, setSelectedRisk] = useState<
    AvailableRisk | RiskWithApplication | null
  >(null);
  const [pendingApplyRisks, setPendingApplyRisks] = useState<AvailableRisk[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: GravidadeDoAviso;
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ============================================================================
  // LOAD OPTIONS
  // ============================================================================

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingOptions(true);
        const response = await companyService.getCompanies({ limit: 1000 });
        setCompanies(
          response.data.map((c) => ({
            id: Number(c.id),
            name: c.tradeName || c.legalName,
          }))
        );
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        setLoadingOptions(false);
      }
    };
    loadCompanies();
  }, []);

  // Unidades e alcance pedem `unit.read`, que a porta da tela não exige: sem ela a tela abre e o
  // nível Unidade diz por que não oferece unidades, em vez de a busca voltar recusada.
  const nivelUnidade = selectedLevel === ApplicationLevel.UNIT;
  const carregarUnidades = useCallback(async (): Promise<SelectOption[]> => {
    if (!selectedCompanyId) return [];
    const response = await unitService.getUnitsByCompanyId(selectedCompanyId);
    return response.map((u) => ({ id: Number(u.id), name: u.name }));
  }, [selectedCompanyId]);
  const unidades = useListaPermitida('unit.read', 'Unidades', carregarUnidades, {
    habilitado: nivelUnidade && Boolean(selectedCompanyId),
  });
  // null enquanto não há resposta: a lista vazia só se anuncia depois de a busca voltar.
  const units =
    nivelUnidade && selectedCompanyId && unidades.resolvida && unidades.permitido && !unidades.erro
      ? unidades.itens
      : null;

  // Who an application to the unit will actually cover
  const carregarAlcance = useCallback(
    async () => (selectedUnitId ? unitService.getAlcance(selectedUnitId) : null),
    [selectedUnitId]
  );
  const alcance = useDadoPermitido('unit.read', 'Alcance da unidade', carregarAlcance, {
    habilitado: nivelUnidade && Boolean(selectedUnitId),
  }).dado;

  // Load departments when company changes
  useEffect(() => {
    if (!selectedCompanyId) {
      setDepartments([]);
      return;
    }

    const loadDepartments = async () => {
      try {
        const response = await departmentService.getDepartments({
          companyId: String(selectedCompanyId),
          limit: 1000,
        });
        setDepartments(
          response.data.map((d) => ({
            id: Number(d.id),
            name: d.name,
            parentId: Number(d.companyId),
          }))
        );
      } catch (err) {
        console.error('Error loading departments:', err);
      }
    };
    loadDepartments();
  }, [selectedCompanyId]);

  // Load occupations when department changes
  useEffect(() => {
    if (!selectedDepartmentId) {
      setOccupations([]);
      return;
    }

    const loadOccupations = async () => {
      try {
        const response = await occupationService.getOccupations({
          limit: 1000,
          filters: { departmentId: selectedDepartmentId },
        });
        setOccupations(
          response.data.map((o) => ({
            id: o.id,
            name: o.name,
            parentId: o.departmentId,
          }))
        );
      } catch (err) {
        console.error('Error loading occupations:', err);
      }
    };
    loadOccupations();
  }, [selectedDepartmentId]);

  // Load employees when occupation changes
  useEffect(() => {
    if (!selectedOccupationId) {
      setEmployees([]);
      return;
    }

    const loadEmployees = async () => {
      try {
        const response = await employeeService.getEmployees({
          limit: 1000,
          filters: { occupationId: selectedOccupationId },
        });
        setEmployees(
          response.data.map((e) => ({
            id: e.id,
            name: e.name,
            parentId: e.occupationId,
          }))
        );
      } catch (err) {
        console.error('Error loading employees:', err);
      }
    };
    loadEmployees();
  }, [selectedOccupationId]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const currentTargetId = useMemo((): number | null => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return selectedCompanyId;
      case ApplicationLevel.UNIT:
        return selectedUnitId;
      case ApplicationLevel.DEPARTMENT:
        return selectedDepartmentId;
      case ApplicationLevel.OCCUPATION:
        return selectedOccupationId;
      case ApplicationLevel.EMPLOYEE:
        return selectedEmployeeId;
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedUnitId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
  ]);

  const currentTargetName = useMemo((): string => {
    switch (selectedLevel) {
      case ApplicationLevel.COMPANY:
        return companies.find((c) => c.id === selectedCompanyId)?.name || '';
      case ApplicationLevel.UNIT:
        return units?.find((u) => u.id === selectedUnitId)?.name || '';
      case ApplicationLevel.DEPARTMENT:
        return departments.find((d) => d.id === selectedDepartmentId)?.name || '';
      case ApplicationLevel.OCCUPATION:
        return occupations.find((o) => o.id === selectedOccupationId)?.name || '';
      case ApplicationLevel.EMPLOYEE:
        return employees.find((e) => e.id === selectedEmployeeId)?.name || '';
    }
  }, [
    selectedLevel,
    selectedCompanyId,
    selectedDepartmentId,
    selectedOccupationId,
    selectedEmployeeId,
    selectedUnitId,
    companies,
    units,
    departments,
    occupations,
    employees,
  ]);

  const riskAlertsCounts = useMemo(() => {
    if (!riskAlerts) return { critical: 0, veryHigh: 0, high: 0 };
    return {
      critical: riskAlerts.summary?.totalCritical || 0,
      veryHigh: riskAlerts.summary?.veryHighCount || 0,
      high: riskAlerts.summary?.highCount || 0,
    };
  }, [riskAlerts]);

  const currentError = activeMainTab === 0 ? riskError : examError;

  // ============================================================================
  // LOAD DATA WHEN TARGET CHANGES
  // ============================================================================

  useEffect(() => {
    if (currentTargetId) {
      // Load both risks and exams when target changes
      loadRisks(selectedLevel, currentTargetId);
      loadExams(selectedLevel, currentTargetId);
    }
  }, [selectedLevel, currentTargetId, loadRisks, loadExams]);

  // Load alerts when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadRiskAlerts(selectedCompanyId);
    }
  }, [selectedCompanyId, loadRiskAlerts]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleLevelChange = (level: ApplicationLevel) => {
    setSelectedLevel(level);
    if (level !== ApplicationLevel.UNIT) setSelectedUnitId(null);
    // Reset dependent selections
    if (level === ApplicationLevel.COMPANY || level === ApplicationLevel.UNIT) {
      setSelectedDepartmentId(null);
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.DEPARTMENT) {
      setSelectedOccupationId(null);
      setSelectedEmployeeId(null);
    } else if (level === ApplicationLevel.OCCUPATION) {
      setSelectedEmployeeId(null);
    }
  };

  // Exam Handlers
  const avisarDaTransferencia = useCallback(
    (message: string, severity: GravidadeDoAviso) => setSnackbar({ open: true, message, severity }),
    []
  );
  // Transferir aplica na hora, como na Aplicação de Exames: cada exame com a periodicidade do próprio
  // cadastro. O painel fica só para editar um exame já aplicado.
  const {
    aplicando: aplicandoExames,
    aplicar: handleExamApply,
    confirmarAntesDeAplicar: confirmarAntesDeAplicarExames,
    dialogoDeConfirmacao: confirmacaoDeAplicarExames,
  } = useAplicarExamesDireto({
    nivel: selectedLevel,
    alvoId: currentTargetId,
    loading: examLoading,
    error: examError,
    applied: examApplied,
    applyMultipleExams,
    loadExams,
    avisar: avisarDaTransferencia,
  });

  const handleExamRemove = useCallback(
    async (items: (AvailableExam | ExamWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is ExamWithApplication => isExamWithApplication(item)
      );

      if (appliedItems.length === 0) return;

      const examIds = appliedItems.map((item) => item.examId);
      const success = await removeMultipleExams(selectedLevel, currentTargetId, examIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${examIds.length} exame(s) removido(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleExams]
  );

  const handleExamEditConfirm = useCallback(
    async (data: ApplicationData) => {
      if (!currentTargetId || !selectedExam || !isExamWithApplication(selectedExam)) return;

      const success = await updateExamApplication(
        selectedLevel,
        currentTargetId,
        selectedExam.examId,
        data
      );

      if (success) {
        setSnackbar({
          open: true,
          message: 'Aplicacao de exame atualizada com sucesso',
          severity: 'success',
        });
        setExamModalOpen(false);
        setSelectedExam(null);
      }
    },
    [currentTargetId, selectedLevel, selectedExam, updateExamApplication]
  );

  const handleExamItemEdit = useCallback(
    (item: AvailableExam | ExamWithApplication) => {
      if (!isExamWithApplication(item)) return;

      setSelectedExam(item);
      setExamModalOpen(true);
    },
    []
  );

  const handleCloseExamModal = useCallback(() => {
    setExamModalOpen(false);
    setSelectedExam(null);
  }, []);

  // Risk Handlers
  const handleRiskApply = useCallback(
    async (items: (AvailableRisk | RiskWithApplication)[]) => {
      const availableItems = items.filter(
        (item): item is AvailableRisk => !isRiskWithApplication(item)
      );

      if (availableItems.length === 0) return;

      setPendingApplyRisks(availableItems);
      setSelectedRisk(availableItems[0]);
      setRiskModalMode('apply');
      setRiskModalOpen(true);
    },
    []
  );

  const handleRiskRemove = useCallback(
    async (items: (AvailableRisk | RiskWithApplication)[]) => {
      if (!currentTargetId) return;

      const appliedItems = items.filter(
        (item): item is RiskWithApplication => isRiskWithApplication(item)
      );

      if (appliedItems.length === 0) return;

      const riskIds = appliedItems.map((item) => item.riskId);
      const success = await removeMultipleRisks(selectedLevel, currentTargetId, riskIds);

      if (success) {
        setSnackbar({
          open: true,
          message: `${riskIds.length} risco(s) removido(s) com sucesso`,
          severity: 'success',
        });
      }
    },
    [currentTargetId, selectedLevel, removeMultipleRisks]
  );

  const levelToScope: Record<ApplicationLevel, RiskApplicationScope> = useMemo(() => ({
    [ApplicationLevel.COMPANY]: 'company',
    [ApplicationLevel.UNIT]: 'unit',
    [ApplicationLevel.DEPARTMENT]: 'department',
    [ApplicationLevel.OCCUPATION]: 'occupation',
    [ApplicationLevel.EMPLOYEE]: 'employee',
  }), []);

  const handleRiskSaved = useCallback(() => {
    if (!currentTargetId) return;
    loadRisks(selectedLevel, currentTargetId);
    setSnackbar({
      open: true,
      message: riskModalMode === 'apply' ? 'Risco aplicado com sucesso' : 'Aplicacao de risco atualizada com sucesso',
      severity: 'success',
    });
    setRiskModalOpen(false);
    setPendingApplyRisks([]);
    setSelectedRisk(null);
  }, [currentTargetId, selectedLevel, loadRisks, riskModalMode]);

  const handleRiskItemEdit = useCallback(
    (item: AvailableRisk | RiskWithApplication) => {
      if (!isRiskWithApplication(item)) return;

      setSelectedRisk(item);
      setRiskModalMode('edit');
      setRiskModalOpen(true);
    },
    []
  );

  const handleCloseRiskModal = useCallback(() => {
    setRiskModalOpen(false);
    setPendingApplyRisks([]);
    setSelectedRisk(null);
  }, []);

  const handleClearError = useCallback(() => {
    if (activeMainTab === 0) {
      clearRiskError();
    } else {
      clearExamError();
    }
  }, [activeMainTab, clearRiskError, clearExamError]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const renderLevelSelector = () => (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Selecionar Nível e Destino" />
      <CardContent>
        <Grid container spacing={3}>
          {/* Level Selector */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel id="nivel-label">Nível</InputLabel>
              <Select
                labelId="nivel-label"
                value={selectedLevel}
                label="Nível"
                disabled={aplicandoExames}
                onChange={(e) => handleLevelChange(e.target.value as ApplicationLevel)}
              >
                {Object.values(ApplicationLevel).map((level) => (
                  <MenuItem key={level} value={level}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {ICONE_DO_NIVEL[level]}
                      <span>{ApplicationLevelLabels[level]}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Company Selector (always shown) */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={selectedCompanyId || ''}
                label="Empresa"
                onChange={(e) => {
                  setSelectedCompanyId(Number(e.target.value));
                  setSelectedUnitId(null);
                  setSelectedDepartmentId(null);
                  setSelectedOccupationId(null);
                  setSelectedEmployeeId(null);
                }}
                disabled={loadingOptions || aplicandoExames}
              >
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Unit Selector (shown for unit only) */}
          {selectedLevel === ApplicationLevel.UNIT && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel id="unidade-label">Unidade</InputLabel>
                <Select
                  labelId="unidade-label"
                  value={units?.some((u) => u.id === selectedUnitId) ? String(selectedUnitId) : ''}
                  label="Unidade"
                  onChange={(e) => {
                    setSelectedUnitId(Number(e.target.value));
                  }}
                  disabled={!selectedCompanyId || !units || units.length === 0 || aplicandoExames}
                >
                  {units?.map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
                {units?.length === 0 && (
                  <FormHelperText>
                    Esta empresa não tem unidades cadastradas.
                    {cadastrarUnidade.liberado && (
                      <>
                        {' '}
                        <Link component={RouterLink} to="/units">
                          Cadastrar unidade
                        </Link>
                      </>
                    )}
                  </FormHelperText>
                )}
                {unidades.bloqueio && <FormHelperText>{unidades.bloqueio}</FormHelperText>}
              </FormControl>
            </Grid>
          )}

          {/* Department Selector (shown for department, occupation, employee) */}
          {selectedLevel !== ApplicationLevel.COMPANY && selectedLevel !== ApplicationLevel.UNIT && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Setor</InputLabel>
                <Select
                  value={selectedDepartmentId || ''}
                  label="Setor"
                  onChange={(e) => {
                    setSelectedDepartmentId(Number(e.target.value));
                    setSelectedOccupationId(null);
                    setSelectedEmployeeId(null);
                  }}
                  disabled={!selectedCompanyId || departments.length === 0 || aplicandoExames}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Occupation Selector (shown for occupation, employee) */}
          {(selectedLevel === ApplicationLevel.OCCUPATION ||
            selectedLevel === ApplicationLevel.EMPLOYEE) && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Cargo</InputLabel>
                <Select
                  value={selectedOccupationId || ''}
                  label="Cargo"
                  onChange={(e) => {
                    setSelectedOccupationId(Number(e.target.value));
                    setSelectedEmployeeId(null);
                  }}
                  disabled={!selectedDepartmentId || occupations.length === 0 || aplicandoExames}
                >
                  {occupations.map((occ) => (
                    <MenuItem key={occ.id} value={occ.id}>
                      {occ.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Employee Selector (shown for employee only) */}
          {selectedLevel === ApplicationLevel.EMPLOYEE && (
            <Grid
              size={{
                xs: 12,
                md: 3
              }}>
              <FormControl fullWidth>
                <InputLabel>Funcionário</InputLabel>
                <Select
                  value={selectedEmployeeId || ''}
                  label="Funcionário"
                  onChange={(e) => setSelectedEmployeeId(Number(e.target.value))}
                  disabled={!selectedOccupationId || employees.length === 0 || aplicandoExames}
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        {/* Current Target Info */}
        {currentTargetId && currentTargetName && (
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={ICONE_DO_NIVEL[selectedLevel]}
              label={`${ApplicationLevelLabels[selectedLevel]}: ${currentTargetName}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        )}
        {selectedLevel === ApplicationLevel.UNIT && selectedUnitId && alcance && (
          <Box sx={{ mt: 2 }}>
            <FaixaDeAlcance alcance={alcance} />
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderRiskTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Riscos"
        subheader={
          currentTargetId
            ? `Aplicar ou remover riscos de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar riscos'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">
            {selectedCompanyId && selectedLevel === ApplicationLevel.UNIT
              ? 'Selecione uma unidade para gerenciar os riscos.'
              : 'Selecione uma empresa e o nivel de aplicacao para gerenciar os riscos.'}
          </Alert>
        ) : riskLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={riskAvailable}
            applied={riskApplied}
            onApply={handleRiskApply}
            onRemove={handleRiskRemove}
            getItemKey={(item) => (isRiskWithApplication(item) ? item.riskId : item.id)}
            getItemLabel={(item) => (isRiskWithApplication(item) ? item.riskName : item.risk)}
            getItemSecondary={(item) => item.riskFactorName}
            searchFields={['riskFactorName', 'hazard']}
            availableTitle="Riscos Disponiveis"
            appliedTitle="Riscos Aplicados"
            loading={riskLoading}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={isRiskWithApplication(item) ? item.riskName : item.risk}
                secondary={
                  isApplied && isRiskWithApplication(item) ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <ExposureLevelBadge
                        level={item.exposureLevel}
                        size="small"
                        exposureDays={item.exposureDays}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {item.riskFactorName}
                      </Typography>
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={item.riskFactorName} size="small" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        {item.hazard}
                      </Typography>
                    </Stack>
                  )
                }
              />
            )}
            onItemEdit={handleRiskItemEdit}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderExamTransferList = () => (
    <Card>
      <CardHeader
        title="Gerenciar Exames"
        subheader={
          currentTargetId
            ? `Aplicar ou remover exames de ${ApplicationLevelLabels[selectedLevel].toLowerCase()}`
            : 'Selecione um destino para gerenciar exames'
        }
      />
      <CardContent>
        {!currentTargetId ? (
          <Alert severity="info">
            {selectedCompanyId && selectedLevel === ApplicationLevel.UNIT
              ? 'Selecione uma unidade para gerenciar os exames.'
              : 'Selecione uma empresa e o nivel de aplicacao para gerenciar os exames.'}
          </Alert>
        ) : examLoading && !aplicandoExames ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DualTransferList
            available={examAvailable}
            applied={examApplied}
            onApply={handleExamApply}
            onBeforeApply={confirmarAntesDeAplicarExames}
            onRemove={handleExamRemove}
            getItemKey={(item) => (isExamWithApplication(item) ? item.examId : item.id)}
            getItemLabel={(item) => item.examName}
            getItemSecondary={(item) =>
              'esocialCode' in item && item.esocialCode ? item.esocialCode : undefined
            }
            searchFields={['examName', 'esocialCode']}
            availableTitle="Exames Disponiveis"
            appliedTitle="Exames Aplicados"
            loading={examLoading && !aplicandoExames}
            disabled={aplicandoExames}
            renderItem={(item, isApplied) => (
              <ListItemText
                primary={item.examName}
                secondary={
                  isApplied && isExamWithApplication(item) ? (
                    <Typography
                      variant="caption"
                      component="span"
                      display="block"
                      color={item.periodicityMonths ? 'text.secondary' : 'warning.main'}
                      sx={{ mt: 0.5 }}
                    >
                      {item.periodicityMonths ? `${item.periodicityMonths} meses` : 'Sem periodicidade'}
                    </Typography>
                  ) : (
                    item.esocialCode ?? undefined
                  )
                }
              />
            )}
            onItemEdit={handleExamItemEdit}
          />
        )}
      </CardContent>
    </Card>
  );

  const renderRiskAlertsTab = () => (
    <Card>
      <CardHeader
        title="Alertas de Exposicao Critica"
        subheader="Riscos com nivel de exposicao alto ou muito alto"
      />
      <CardContent>
        {!selectedCompanyId ? (
          <Alert severity="info">
            Selecione uma empresa para visualizar os alertas de exposicao critica.
          </Alert>
        ) : riskLoadingAlerts ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : riskAlertsError ? (
          <Alert severity="error" onClose={clearRiskAlertsError}>
            {riskAlertsError}
          </Alert>
        ) : !riskAlerts || riskAlerts.critical.length === 0 ? (
          <Alert severity="success">
            Nenhum risco com nivel de exposicao critico encontrado.
          </Alert>
        ) : (
          <Box>
            {/* Summary */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
              <Chip
                label={`Total: ${riskAlertsCounts.critical}`}
                color="default"
              />
              <Chip
                label={`Muito Alto: ${riskAlertsCounts.veryHigh}`}
                color="error"
              />
              <Chip
                label={`Alto: ${riskAlertsCounts.high}`}
                color="warning"
              />
            </Box>

            {/* Critical Alerts List */}
            {riskAlerts.critical.map((alert) => (
              <Card
                key={`critical-${alert.applicationId}`}
                variant="outlined"
                sx={{
                  mb: 1,
                  borderColor:
                    alert.exposureLevel === ExposureLevel.VERY_HIGH
                      ? 'error.main'
                      : 'warning.main',
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="subtitle2">{alert.riskName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ApplicationLevelLabels[alert.targetType]}: {alert.targetName}
                      </Typography>
                    </Box>
                    <ExposureLevelBadge
                      level={alert.exposureLevel}
                      exposureDays={alert.exposureDays}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Vencimento é de cada funcionário: quem mostra vencidos e a vencer é a Convocação (#567).
  const destinoDaConvocacao = selectedCompanyId
    ? `/exam-summons?status=overdue&companyId=${selectedCompanyId}`
    : '/exam-summons?status=overdue';
  const renderLinhaDaConvocacao = () => (
    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
      Vencidos e a vencer, funcionário por funcionário, ficam na Convocação de Exames.
      {!abrirConvocacao.disabled && (
        <>
          {' '}
          <Link component={RouterLink} to={destinoDaConvocacao}>
            Abrir Convocação de Exames
          </Link>
        </>
      )}
    </Typography>
  );

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Seguranca', path: '/seguranca' },
        { label: 'Migracao de Riscos e Exames' },
      ]}
      title="Migracao de Riscos e Exames"
      subtitle="Gerencie a aplicacao de riscos e exames em empresas, unidades, setores, cargos e funcionarios de forma unificada"
    >
      {/* Error Alert */}
      {currentError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleClearError}>
          {currentError}
        </Alert>
      )}

      {/* Level Selector */}
      {renderLevelSelector()}

      {/* Main Tabs (Risks / Exams) */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeMainTab} onChange={(_, newValue) => setActiveMainTab(newValue)}>
          <Tab
            label={
              <Badge
                badgeContent={riskAlertsCounts.critical}
                color="error"
                max={99}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon fontSize="small" />
                  Riscos
                </Box>
              </Badge>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ExamIcon fontSize="small" />
                Exames
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Risks Tab Panel */}
      <TabPanel value={activeMainTab} index={0}>
        {/* Sub-tabs for Risks */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeSubTab} onChange={(_, newValue) => setActiveSubTab(newValue)}>
            <Tab label="Gerenciar Riscos" />
            <Tab
              label={
                <Badge
                  badgeContent={riskAlertsCounts.critical}
                  color="error"
                  max={99}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon fontSize="small" />
                    Alertas Criticos
                  </Box>
                </Badge>
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={activeSubTab} index={0}>
          {renderRiskTransferList()}
        </TabPanel>

        <TabPanel value={activeSubTab} index={1}>
          {renderRiskAlertsTab()}
        </TabPanel>
      </TabPanel>

      {/* Exams Tab Panel: sem sub-abas, a lista aparece qualquer que seja a sub-aba de riscos escolhida */}
      <TabPanel value={activeMainTab} index={1}>
        {renderExamTransferList()}
        {renderLinhaDaConvocacao()}
      </TabPanel>

      {/* Exam Modal */}
      <ExamApplicationModal
        open={examModalOpen}
        onClose={handleCloseExamModal}
        onConfirm={handleExamEditConfirm}
        exam={selectedExam}
        mode="edit"
        loading={examLoading}
        error={examError}
      />

      {confirmacaoDeAplicarExames}

      {/* Risk Modal */}
      {riskModalOpen && currentTargetId != null && (
        riskModalMode === 'apply' && pendingApplyRisks.length > 0 ? (
          <RiskApplicationModal
            open={riskModalOpen}
            onClose={handleCloseRiskModal}
            onSaved={handleRiskSaved}
            scope={levelToScope[selectedLevel]}
            scopeId={currentTargetId}
            mode="apply"
            riskId={pendingApplyRisks[0].id}
            riskName={pendingApplyRisks[0].risk}
            riskFactorName={pendingApplyRisks[0].riskFactorName}
          />
        ) : riskModalMode === 'edit' && selectedRisk != null && isRiskWithApplication(selectedRisk) ? (
          <RiskApplicationModal
            open={riskModalOpen}
            onClose={handleCloseRiskModal}
            onSaved={handleRiskSaved}
            scope={levelToScope[selectedLevel]}
            scopeId={currentTargetId}
            mode="edit"
            applicationId={selectedRisk.id}
            riskName={selectedRisk.riskName}
            riskFactorName={selectedRisk.riskFactorName}
          />
        ) : null
      )}

      {/* Snackbar: o de sucesso some sozinho; erro e alerta ficam até a pessoa fechar no X. */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'success' ? 5000 : null}
        onClose={() => {
          if (snackbar.severity === 'success') setSnackbar((prev) => ({ ...prev, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default RiskExamMigrationPage;
